// src/features/public/PublicBookingModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, X, Loader2 } from "lucide-react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase";
import { TENANT_ID } from "../../tenant";

import Step1_ServiceSelect from "../dashboard/components/Booking/steps/Step1_ServiceSelect";
import Step2_DateTimeSelect from "../dashboard/components/Booking/steps/Step2_DateTimeSelect";

const PublicBookingModal = ({
  isOpen,
  onClose,
  services = [],
  isLoading = false,
  onProceedAuth,
}) => {
  const [step, setStep] = useState(1);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlots, setSelectedSlots] = useState([]); // ✅ Date[]
  const [displayMonth, setDisplayMonth] = useState(new Date());

  const [existingReservations, setExistingReservations] = useState([]);
  const [selectedTier, setSelectedTier] = useState(null);
  const [guestCount, setGuestCount] = useState(1);

  const scrollRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setSelectedService(null);
      setSelectedDate(null);
      setSelectedSlots([]);
      setDisplayMonth(new Date());
      setExistingReservations([]);
      setSelectedTier(null);
      setGuestCount(1);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
    });
  }, [isOpen, step]);

  // ✅ service が変わった時だけ初期 tier を決める
  useEffect(() => {
    if (!selectedService) {
      setSelectedTier(null);
      return;
    }

    if (
      Array.isArray(selectedService.price_tiers) &&
      selectedService.price_tiers.length > 0
    ) {
      setSelectedTier(selectedService.price_tiers[0]);
      return;
    }

    setSelectedTier(null);
  }, [selectedService]);

  useEffect(() => {
    const fetchExistingReservations = async () => {
      if (!selectedService) return;

      try {
        const fixedList = Array.isArray(selectedService.fixed_slots)
          ? selectedService.fixed_slots
          : [];
        const timeRanges = Array.isArray(selectedService.time_ranges)
          ? selectedService.time_ranges
          : [];
        const slotIntervalRaw = selectedService.slot_interval;

        const isFixedMode =
          fixedList.length > 0 ||
          (timeRanges.length === 0 && Number(slotIntervalRaw) === 0) ||
          Number(slotIntervalRaw) === 0;

        // 他人の予約は直接読めない(rules)。日時・人数・状態だけ返す callable を使う
        const payload = { tenantId: TENANT_ID, serviceId: selectedService.id };

        if (!isFixedMode) {
          if (!selectedDate) {
            setExistingReservations([]);
            return;
          }

          const startOfDay = new Date(selectedDate);
          startOfDay.setHours(0, 0, 0, 0);

          const endOfDay = new Date(selectedDate);
          endOfDay.setHours(23, 59, 59, 999);

          payload.from = startOfDay.toISOString();
          payload.to = endOfDay.toISOString();
        }

        const res = await httpsCallable(functions, "getReservationAvailability")(payload);

        const bookings = (res.data?.reservations || [])
          .filter((res) => {
            const isSameService = res.serviceId === selectedService.id;
            const isValidStatus =
              res.status === "confirmed" || res.status === "pending_payment";
            return isSameService && isValidStatus;
          });

        setExistingReservations(bookings);
      } catch (e) {
        console.error("[PublicBookingModal] 予約状況取得エラー:", e);
        setExistingReservations([]);
      }
    };

    fetchExistingReservations();
  }, [selectedService, selectedDate]);

  const formatDay = (date) => ["日", "月", "火", "水", "木", "金", "土"][date.getDay()];

  const formatISODate = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
    return (
      date.getFullYear() +
      "-" +
      String(date.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(date.getDate()).padStart(2, "0")
    );
  };

  const getDaysInDisplayMonth = () => {
    const year = displayMonth.getFullYear();
    const month = displayMonth.getMonth();
    const lastDay = new Date(year, month + 1, 0).getDate();
    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let d = 1; d <= lastDay; d++) {
      const date = new Date(year, month, d);
      if (date >= today) days.push(date);
    }
    return days;
  };

  const changeMonth = (delta) => {
    setDisplayMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
  };

  const parseFixedSlot = (slot) => {
    if (!slot?.date || !slot?.start) return null;
    const [year, month, day] = String(slot.date).split("-").map(Number);
    const [hour, minute] = String(slot.start).split(":").map(Number);
    return new Date(year, month - 1, day, hour, minute);
  };

  const getTimeSlots = () => {
    if (!selectedService || !selectedDate) return [];

    const fixedList = Array.isArray(selectedService.fixed_slots)
      ? selectedService.fixed_slots
      : [];
    const timeRanges = Array.isArray(selectedService.time_ranges)
      ? selectedService.time_ranges
      : [];
    const slotIntervalRaw = selectedService.slot_interval;

    const isFixedMode =
      fixedList.length > 0 ||
      (timeRanges.length === 0 && Number(slotIntervalRaw) === 0) ||
      Number(slotIntervalRaw) === 0;

    if (isFixedMode) {
      const targetDateStr = formatISODate(selectedDate);

    return fixedList
      .filter((slot) => String(slot?.date || "") === targetDateStr)
      .map((slot) => {
        const d = parseFixedSlot(slot);
        if (!d) return null;
        return d;
      })
      .filter((d) => {
        if (!d) return false;
        const endTime = new Date(d.getTime() + Number(selectedService?.duration || 60) * 60000);
        return endTime > new Date();
      })
      .sort((a, b) => a - b);
    }

    const slots = [];
    const ranges =
      timeRanges.length > 0
        ? timeRanges
        : [
            {
              start: selectedService?.available_start || "10:00",
              end: selectedService?.available_end || "20:00",
            },
          ];

    const interval = Number(selectedService?.slot_interval ?? 60);
    const duration = Number(selectedService?.duration ?? 60);

    if (!Number.isFinite(interval) || interval <= 0) return [];
    if (!Number.isFinite(duration) || duration <= 0) return [];

    ranges.forEach((range) => {
      const [startH, startM] = String(range.start || "10:00").split(":").map(Number);
      const [endH, endM] = String(range.end || "20:00").split(":").map(Number);

      let current = new Date(selectedDate);
      current.setHours(startH, startM, 0, 0);

      const limit = new Date(selectedDate);
      limit.setHours(endH, endM, 0, 0);

      while (new Date(current.getTime() + duration * 60000) <= limit) {
        slots.push(new Date(current));
        current = new Date(current.getTime() + interval * 60000);
      }
    });

    return slots.sort((a, b) => a - b);
  };

  const getSlotStatus = (slotTime) => {
    if (!selectedService) {
      return { disabled: false, text: "" };
    }

    if (!(slotTime instanceof Date) || Number.isNaN(slotTime.getTime())) {
      return { disabled: true, text: "" };
    }

    if (slotTime < new Date()) {
      return { disabled: true, text: "受付終了" };
    }

    const capacity = Number(selectedService.capacity || 1);
    const capType = selectedService.capacity_type || "group";

    const slotStart = slotTime.getTime();
    const slotEnd =
      slotStart + Number(selectedService.duration || 60) * 60000;

    const conflictingReservations = existingReservations.filter((res) => {
      const resStart = res.startAt?.toDate
        ? res.startAt.toDate().getTime()
        : new Date(res.startAt).getTime();

      const resEnd = res.endAt?.toDate
        ? res.endAt.toDate().getTime()
        : new Date(res.endAt).getTime();

      return resStart < slotEnd && resEnd > slotStart;
    });

    let reservedCount = 0;
    let currentBookingCount = 0;

    if (capType === "person") {
      reservedCount = conflictingReservations.reduce(
        (sum, r) => sum + (Number(r.guestCount) || 1),
        0
      );
      currentBookingCount = Math.max(1, Number(guestCount || 1));
    } else {
      reservedCount = conflictingReservations.length;
      currentBookingCount = 1;
    }

    const isFull = reservedCount + currentBookingCount > capacity;
    const isPartiallyFull = reservedCount > 0 && reservedCount < capacity;

    if (isFull) {
      return {
        disabled: true,
        text: "満席",
        reserved: reservedCount,
        capacity,
      };
    }

    return {
      disabled: false,
      text: isPartiallyFull ? `残${capacity - reservedCount}` : "",
      reserved: reservedCount,
      capacity,
    };
  };

  const isSlotFull = (slotTime) => {
    return getSlotStatus(slotTime).disabled;
  };

  const handleFixedSlotSelect = (slotDate) => {
    setSelectedDate(slotDate);
    setSelectedSlots([slotDate]);
    setDisplayMonth(new Date(slotDate.getFullYear(), slotDate.getMonth(), 1));
    setStep(3);
  };

  const toggleSlot = (time) => {
    if (!selectedService) return;

    const status = getSlotStatus(time);
    if (status.disabled) return;

    const allowMultiple = !!selectedService.allow_multiple_slots;

    setSelectedSlots((prev) => {
      const exists = prev.some((t) => t.getTime() === time.getTime());

      if (!allowMultiple) {
        if (exists) return [];
        return [time];
      }

      if (exists) {
        return prev.filter((t) => t.getTime() !== time.getTime());
      }

      return [...prev, time].sort((a, b) => a - b);
    });
  };

  const getTierPrice = (tier) => {
    if (!tier) return Number(selectedService?.price || 0);
    return Number(tier?.price || 0);
  };

  const availableSlots = useMemo(() => {
    return getTimeSlots();
  }, [selectedService, selectedDate, existingReservations, guestCount]);

  const overlayClass = [
    "fixed inset-0 z-[13000] flex items-end sm:items-center justify-center p-0 sm:p-4",
    "transition-all duration-200 ease-out",
    isOpen ? "opacity-100" : "opacity-0 pointer-events-none",
    "bg-black/60 backdrop-blur-md",
  ].join(" ");

  const panelClass = [
    "w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col relative h-[90vh] sm:h-auto sm:max-h-[90vh]",
    "transition-all duration-200 ease-out",
    isOpen ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-2 scale-[0.985]",
  ].join(" ");

  const panelStyle = {
    maxHeight: "calc(100dvh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 32px)",
  };

  if (!isOpen) return null;

  return (
    <div className={overlayClass} aria-hidden={!isOpen}>
      <div className={panelClass} style={panelStyle} role="dialog" aria-modal="true">
        <div
          className="pt-6 pb-4 px-6 bg-white border-b border-gray-50 flex items-center justify-between sticky top-0 z-10"
          style={{ paddingTop: "calc(1.5rem + env(safe-area-inset-top))" }}
        >
          {step > 1 ? (
            <button
              onClick={() => setStep((prev) => Math.max(1, prev - 1))}
              className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ChevronLeft size={24} />
            </button>
          ) : (
            <div className="w-10" />
          )}

          <h1 className="font-black text-lg tracking-tight">
            {step === 1 && "メニュー選択"}
            {step === 2 && (selectedService?.name || "日時選択")}
            {step === 3 && "内容確認"}
          </h1>

          <button
            onClick={onClose}
            className="p-2 -mr-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500"
          >
            <X size={24} />
          </button>
        </div>

        <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden bg-gray-50">
          {step === 1 && (
            <Step1_ServiceSelect
              services={services}
              onSelect={(service) => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                setSelectedService(service);
                setSelectedDate(today); // ✅ 今日を初期選択
                setDisplayMonth(new Date(today.getFullYear(), today.getMonth(), 1));
                setSelectedSlots([]);
                setGuestCount(1);
                setStep(2);
              }}
              isLoading={isLoading}
              checkIsMember={() => false}
            />
          )}

          {step === 2 && selectedService && (
            <Step2_DateTimeSelect
              service={selectedService}
              date={selectedDate || new Date()}
              setDate={(d) => {
                setSelectedDate(d);
                setDisplayMonth(new Date(d.getFullYear(), d.getMonth(), 1));
                setSelectedSlots([]);
              }}
              availableSlots={availableSlots}
              selectedSlots={selectedSlots}
              toggleSlot={toggleSlot}
              isSlotFull={isSlotFull}
              tier={selectedTier}
              setTier={setSelectedTier}
              guestCount={guestCount}
              setGuestCount={setGuestCount}
              fixedSlots={selectedService.fixed_slots || []}
              parseFixedSlot={parseFixedSlot}
              handleFixedSlotSelect={handleFixedSlotSelect}
              displayMonth={displayMonth}
              changeMonth={changeMonth}
              getDaysInDisplayMonth={getDaysInDisplayMonth}
              formatDay={formatDay}
              isMember={false}
              getTierPrice={getTierPrice}
              goToNext={() => setStep(3)}
            />
          )}

          {step === 3 && selectedService && selectedDate && selectedSlots.length > 0 && (
            <div className="p-6 space-y-6 animate-in slide-in-from-right duration-300 pb-20">
              <div className="bg-white p-8 rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 text-center space-y-4">
                <div>
                  <div className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">
                    メニュー
                  </div>
                  <h2 className="text-2xl font-black text-gray-800">
                    {selectedService.name}
                  </h2>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 text-sm space-y-3 text-left">
                  <div className="flex justify-between font-bold text-gray-600">
                    <span>日付</span>
                    <span>{selectedDate.getMonth() + 1}月{selectedDate.getDate()}日（{formatDay(selectedDate)}）</span>
                  </div>

                  <div className="flex justify-between items-start font-bold text-gray-600">
                    <span>時間</span>
                    <div className="text-right">
                      {selectedSlots.map((slot, idx) => (
                        <div key={idx}>
                          {String(slot.getHours()).padStart(2, "0")}:
                          {String(slot.getMinutes()).padStart(2, "0")}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-between font-bold text-gray-600">
                    <span>利用時間</span>
                    <span>{selectedSlots.length * Number(selectedService.duration || 0)}分</span>
                  </div>

                  {selectedService.capacity_type === "person" ? (
                    <div className="flex justify-between font-bold text-gray-600">
                      <span>人数</span>
                      <span>{guestCount}名</span>
                    </div>
                  ) : selectedTier ? (
                    <div className="flex justify-between font-bold text-gray-600">
                      <span>プラン</span>
                      <span>{selectedTier.min}〜{selectedTier.max}名様</span>
                    </div>
                  ) : (
                    <div className="flex justify-between font-bold text-gray-600">
                      <span>予約種別</span>
                      <span>1枠予約</span>
                    </div>
                  )}

                  <div className="flex justify-between font-bold text-gray-600">
                    <span>1枠料金</span>
                    <span>¥{getTierPrice(selectedTier).toLocaleString()}</span>
                  </div>

                  <div className="border-t border-gray-200 pt-2 flex justify-between font-black text-gray-900 text-lg">
                    <span>合計</span>
                    <span>
                      ¥{(
                        getTierPrice(selectedTier) *
                        selectedSlots.length *
                        (selectedService.capacity_type === "person" ? guestCount : 1)
                      ).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-100 rounded-3xl p-4">
                <p className="text-[12px] font-black text-gray-800 mb-1">
                  このまま予約に進むにはログインが必要です
                </p>
                <p className="text-[11px] text-gray-500 leading-relaxed">
                  ログイン後に予約内容を引き継いで続行します。
                </p>
              </div>

              <button
                type="button"
                onClick={() =>
                  onProceedAuth?.({
                    serviceId: selectedService.id,
                    serviceName: selectedService.name || "",
                    date: selectedDate.toISOString(),
                    displayMonth: new Date(
                      displayMonth.getFullYear(),
                      displayMonth.getMonth(),
                      1
                    ).toISOString(),
                    slots: selectedSlots.map((slot) => ({
                      iso: slot.toISOString(),
                      label: slot.toLocaleTimeString("ja-JP", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                      }),
                    })),
                    guestCount,
                    tier: selectedTier
                      ? {
                          min: Number(selectedTier.min ?? 1),
                          max: Number(selectedTier.max ?? 1),
                          price: Number(selectedTier.price ?? 0),
                          member_price: Number(
                            selectedTier.member_price ?? selectedTier.price ?? 0
                          ),
                        }
                      : null,
                  })                }
                className="w-full py-4 rounded-3xl bg-gray-900 text-white font-black"
              >
                ログインして予約に進む
              </button>
            </div>
          )}

          {step === 1 && isLoading && (
            <div className="py-20 flex items-center justify-center flex-col gap-2">
              <Loader2 className="animate-spin text-gray-900" size={36} />
              <span className="text-xs font-bold text-gray-500">読み込み中...</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicBookingModal;